import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { buttonSize, buttonVariant } from '@models/button-options.enum';
import { Vessel } from 'icreate/common/models/vessel';
import { Mimic } from 'icreate/common/models/mimic';
import { CanvasService } from '@service/canvas/canvas.service';
import { MimicsService } from '@service/mimics.service';
import dayjs from 'dayjs';
import { MatDialog } from '@angular/material/dialog';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';
import { AutosaveErrorDialogComponent } from '@components/autosave-error-dialog/autosave-error-dialog.component';
import { ContactService } from '@service/contact.service';
import { AuthService } from '@service/auth.service';
import { UndoManagerService } from '@service/canvas/undoMgr.service';
import { RuntimeEnvironments } from '@src/app/models/runtime-enviroment.enum';
import { EnvironmentService } from '@service/environment.service';
import { RootItem, RootItemType } from 'icreate/common/models/mimic-folder';
import { AutosaveControlService, AutosavePage } from '@service/auto-save-controller.service';
import { EditorMimicActionsService } from '@service/editor-mimic-actions.service';
import { EditorService, MenuTabs } from '@service/editor.service';

@Component({
    selector: 'app-editor-header',
    templateUrl: './editor-header.component.html',
    styleUrls: ['./editor-header.component.scss'],
    standalone: false,
})
export class EditorHeaderComponent implements OnInit, OnDestroy, OnChanges {
    @Input() vessel: Vessel;
    @Input() selectedMimic: Mimic;
    @Input() userCanEdit: boolean;
    @Input() rootList: RootItem[];
    @Input() mimicName: string;
    @Output() onSaveRequest: EventEmitter<void> = new EventEmitter();
    @Output() onGoBack: EventEmitter<void> = new EventEmitter();
    @Output() onShowPreview: EventEmitter<void> = new EventEmitter();
    @Output() onMimicSaved: EventEmitter<string> = new EventEmitter();

    readonly BUTTON_SMALL_SIZE = buttonSize.small;
    readonly BUTTON_TEXT = buttonVariant.text;

    isAutosaveEnabled: boolean = true;
    mimicNameIsRepeated: boolean = true;

    autosaveStatus: AutoSaveStatus = AutoSaveStatus.UNDEFINED;
    lastSaveTime: string;
    lastSaveDate: Date;
    selectedMenu: MenuTabs;

    saveInterval;

    destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        private canvasService: CanvasService,
        private mimicService: MimicsService,
        private dialog: MatDialog,
        private contactService: ContactService,
        private authService: AuthService,
        private undoManagerService: UndoManagerService,
        private environmentService: EnvironmentService,
        private autosaveControlService: AutosaveControlService,
        private editorMimicActionsService: EditorMimicActionsService,
        private editorService: EditorService
    ) {
        this.isAutosaveEnabled = environmentService.getEnvironment() != RuntimeEnvironments.LOCAL;
    }

    ngOnInit(): void {
        this.editorService.currentSelectedTab$
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((tab: MenuTabs) => {
                this.selectedMenu = tab;
            });
    }

    ngOnDestroy(): void {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
        if (this.saveInterval) {
            clearInterval(this.saveInterval);
            this.saveInterval = null;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.selectedMimic && changes.selectedMimic) {
            if (changes.selectedMimic.previousValue?.id == changes.selectedMimic.currentValue?.id) {
                return;
            }
            this.autosaveStatus = AutoSaveStatus.UNDEFINED;
            this.isAutosaveEnabled = true;
            this.lastSaveTime = null;
            this.lastSaveDate = null;
            if (this.saveInterval) {
                clearInterval(this.saveInterval);
                this.saveInterval = null;
            }
            if (this.isAutosaveEnabled && this.userCanEdit) {
                this.startSaveInterval();
            }
        }
        if (!this.userCanEdit) {
            this.isAutosaveEnabled = false;
        } else {
            this.isAutosaveEnabled = true;
        }

        if (changes.mimicName) {
            this.checkMimicName();
        }

        this.autosaveControlService
            .getDialogState(AutosavePage.MIMIC)
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(isOpen => {
                if (isOpen) {
                    if (this.saveInterval) {
                        clearInterval(this.saveInterval);
                        this.saveInterval = null;
                    }
                } else {
                    this.startSaveInterval();
                }
            });
    }

    undo() {
        this.undoManagerService.handleUndo();
    }

    redo() {
        this.undoManagerService.handleRedo();
    }

    backButtonClick() {
        this.onGoBack.emit();
    }

    saveMimic() {
        if (!this.userCanEdit) return;
        if (this.vessel && this.vessel.id && this.selectedMimic?.id) {
            this.onSaveRequest.emit();
        }
    }

    previewMimic() {
        if (this.vessel && this.vessel.id && this.selectedMimic?.id) {
            this.onShowPreview.emit();
        }
    }

    checkMimicName() {
        this.mimicNameIsRepeated = false;
        if (!this.rootList || this.rootList.length == 0) return;

        const mimics: Mimic[] = [];
        this.rootList?.forEach(item => {
            if (item.type === RootItemType.mimic) {
                mimics.push(item.mimic);
            } else if (item.type === RootItemType.folder) {
                mimics.push(...(item.folder?.mimicList ?? []));
            }
        });

        if (mimics.length == 0) return;

        this.mimicNameIsRepeated = mimics.some(
            mimic => mimic.id !== this.selectedMimic?.id && mimic.name == this.mimicName
        );
    }

    updateAutosave(event) {
        this.isAutosaveEnabled = event.checked;
        if (this.isAutosaveEnabled) this.startSaveInterval();
        else {
            clearInterval(this.saveInterval);
            this.saveInterval = null;
        }
    }

    getAutosaveIcon() {
        if (this.autosaveStatus == AutoSaveStatus.SAVING || this.autosaveStatus == AutoSaveStatus.DELAYED) {
            return 'loading-save';
        } else if (this.autosaveStatus == AutoSaveStatus.SAVED) {
            return 'tick';
        } else if (this.autosaveStatus == AutoSaveStatus.ERROR) {
            return 'save-error';
        }
    }

    getAutosaveStatus() {
        if (this.autosaveStatus == AutoSaveStatus.SAVING) {
            return 'mimic.header.autosave.status.saving';
        } else if (this.autosaveStatus == AutoSaveStatus.DELAYED) {
            return 'mimic.header.autosave.status.delayed';
        } else if (this.autosaveStatus == AutoSaveStatus.SAVED) {
            return 'mimic.header.autosave.status.saved';
        } else if (this.autosaveStatus == AutoSaveStatus.ERROR) {
            return 'mimic.header.autosave.status.error';
        }
    }

    startSaveInterval() {
        if (this.saveInterval || !this.userCanEdit) return;

        this.saveInterval = setInterval(async () => {
            this.autoSaveMimic();
        }, 60000);
    }

    async autoSaveMimic() {
        if (
            this.autosaveStatus === AutoSaveStatus.SAVING ||
            !this.userCanEdit ||
            this.selectedMenu == MenuTabs.SETTINGS
        )
            return;

        let delayInterval = setInterval(async () => {
            this.autosaveStatus = AutoSaveStatus.DELAYED;
        }, 300000);

        const currentSvgContent = this.editorMimicActionsService.serializeCurrentSvg();
        if (this.selectedMimic.svgcontent != currentSvgContent || this.autosaveStatus === AutoSaveStatus.ERROR) {
            try {
                this.autosaveStatus = AutoSaveStatus.SAVING;
                this.selectedMimic.svgcontent = currentSvgContent;
                await this.mimicService.update(this.selectedMimic, true);
                this.autosaveStatus = AutoSaveStatus.SAVED;
                this.onMimicSaved.emit(currentSvgContent);
            } catch (err) {
                this.autosaveStatus = AutoSaveStatus.ERROR;
                console.error(err);
                clearInterval(delayInterval);
                delayInterval = null;
            }
        } else if (this.selectedMimic.svgcontent == currentSvgContent) {
            this.autosaveStatus = AutoSaveStatus.SAVED;
        }

        if (this.autosaveStatus === AutoSaveStatus.SAVED) {
            this.updateSaveTime();
        }

        clearInterval(delayInterval);
        delayInterval = null;
    }

    updateSaveTime() {
        this.autosaveStatus = AutoSaveStatus.SAVED;
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const hour = dayjs().hour();
        const minutes = dayjs().minute().toString().padStart(2, '0');
        this.lastSaveTime = hour + ':' + minutes + ' ' + timezone;
        this.lastSaveDate = new Date();
    }

    hasSaveError() {
        return this.autosaveStatus === AutoSaveStatus.ERROR;
    }

    async openErrorMessage() {
        const dialogRef = this.dialog.open(AutosaveErrorDialogComponent, {
            width: '442px',
            data: { projectVersion: this.vessel.projectVersion },
        });
        const shouldSendError = await lastValueFrom(dialogRef.afterClosed());

        if (shouldSendError) {
            try {
                const receiverAddress = this.authService.getCurrentUser().email;
                await this.contactService.sendErrorReport(receiverAddress);
            } catch (err) {
                console.error(err);
            }
        }
    }
}

export enum AutoSaveStatus {
    UNDEFINED = 'undefined',
    SAVING = 'saving',
    SAVED = 'saved',
    DELAYED = 'delayed',
    ERROR = 'error',
}
